import { getPageContent, PageContentResponse } from 'application/repositories/pageContentRepository';
import { SiteSettingsResponse } from 'application/repositories/siteSettingsRepository';
import { getNavigation, NavigationResponse } from 'application/repositories/navigationRepository';
import { LayoutFeature } from 'features/Layouts/Layout/LayoutFeature';
import { PageTypeMapFeature } from 'features/Mappers/PageType/PageTypeFeature';
import { getAccessToken } from 'helpers/auth';
import { ApiRedirect } from 'helpers/error';
import logger from 'helpers/logger';
import { GetServerSideProps, NextPage } from 'next';
import { getServerSession, Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import React from 'react';
import { authOptions } from '../../api/auth/[...nextauth]';

const sanitize = (obj: unknown) => JSON.parse(JSON.stringify(obj, (_key, value) => (value === undefined ? null : value)));

const toSiteSettings = (content: PageContentResponse): SiteSettingsResponse => ({
	root: { name: content?.root?.name ?? '', properties: content?.root?.properties ?? {} },
	siteSettings: content?.siteSettings ?? [],
});

const Page: NextPage<PageContentResponse> = (pageContent) => {
	const { page } = pageContent ?? {};

	return (
		<LayoutFeature {...pageContent}>
			<PageTypeMapFeature {...page} />
		</LayoutFeature>
	);
};

type PageProps = {
	host: string | string[];
	content: Umbraco.PageContent;
	siteSettings: SiteSettingsResponse | null;
	navigation: NavigationResponse | null;
	session: Session | null;
};

export const getServerSideProps: GetServerSideProps<PageProps> = async (context) => {
	const { params, preview, previewData: untypedPreviewData, req } = context;
	const { host } = params;

	try {
		const url = params.page ? (params.page as string[]).join('/') : '';
		const accessToken = await getAccessToken(req);

		const content = await getPageContent({
			url,
			host,
			preview,
			previewData: untypedPreviewData as Models.PreviewData,
			accessToken,
		});

		const navigation = await getNavigation({ host, preview, previewData: untypedPreviewData as Models.PreviewData, accessToken });

		const serverSession = await getServerSession(context.req, context.res, authOptions);

		const returnData = {
			props: sanitize({
				host,
				content,
				siteSettings: toSiteSettings(content),
				navigation,
				session: serverSession,
			}),
		};

		return returnData;
	} catch (err) {
		if (err instanceof ApiRedirect) {
			throw err;
		}
		if (err?.statusCode === 404) {
			return { notFound: true };
		}
		if (err?.statusCode === 401 || err?.statusCode === 403) {
			const pageContent = err.content as PageContentResponse;
			const session = await getSession(context);
			// const navigation = await getNavigation({ host, preview, previewData: untypedPreviewData as Models.PreviewData });

			return {
				props: sanitize({
					host,
					content: pageContent,
					siteSettings: toSiteSettings(pageContent),
					session,
				}),
			};
		}
		logger.error(err);
		throw new Error(err);
	}
};

export default Page;
